/**
 * TransitionWindowStrip
 *
 * A calm orientation strip for the candidate dashboard, anchoring a
 * transitioning service member's view in time:
 *
 *     Today  ──────────  Transition  ──────────  +90 days
 *
 * SOURCE OF TRUTH: the user's explicit account field `transition_date`
 * (passed in as `serverDate`). A legacy device-only value in localStorage
 * (`st_transition_date`) is used ONLY as a temporary display fallback and
 * as a prefill for the edit dialog, so a user does not lose a date they
 * set before this field existed. Once the user saves, the backend becomes
 * canonical and the localStorage fallback is cleared.
 *
 * NO INFERENCE: a transition date is shown only when it is an explicit,
 * stored value. It is never derived from rank, service history, MOS/AOC,
 * the current date, or any resume data. When none exists we show a neutral
 * "Transition date not set" state.
 *
 * MATCH SAFETY: `transition_date` lives on the user account document, never
 * on the resume; it is excluded from all scoring evidence, hashing, and
 * versioning. Saving it cannot invalidate Formula C scores or trigger
 * materialization.
 */

import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const STORAGE_KEY = 'st_transition_date';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function isValidIsoDate(s) {
  if (!s || typeof s !== 'string') return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(s + 'T00:00:00');
  return !isNaN(d.getTime());
}

function readLocalFallback() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isValidIsoDate(stored) ? stored : null;
  } catch {
    return null;
  }
}

function formatLong(d) {
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function addDays(d, days) {
  const out = new Date(d);
  out.setDate(out.getDate() + days);
  return out;
}

// Whole days from today (midnight) until the given date. Positive = future.
function daysUntil(target) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const ms = target.getTime() - today.getTime();
  return Math.round(ms / 86400000);
}

function remainingLabel(target) {
  const days = daysUntil(target);
  if (days > 0) {
    if (days >= 60) return `${Math.round(days / 30)} months remaining`;
    return `${days} ${days === 1 ? 'day' : 'days'} remaining`;
  }
  if (days === 0) return 'Today';
  return 'Date has passed';
}

export function TransitionWindowStrip({ variant = 'full', onChange, serverDate = null }) {
  // Explicit values only. `serverDate` (account field) is canonical; the
  // localStorage value is a temporary fallback until the user saves.
  const [transitionDate, setTransitionDate] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Server wins. Fall back to a legacy device-only value only if the
    // account has no explicit date yet.
    if (isValidIsoDate(serverDate)) {
      setTransitionDate(serverDate);
    } else {
      setTransitionDate(readLocalFallback());
    }
  }, [serverDate]);

  const openDialog = () => {
    setDraft(transitionDate || '');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!isValidIsoDate(draft) || saving) return;
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/auth/transition-date`, { transition_date: draft }, { withCredentials: true });
      // Backend is now canonical; drop the legacy device-only fallback.
      try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      setTransitionDate(draft);
      setDialogOpen(false);
      if (typeof onChange === 'function') onChange(draft);
    } catch {
      /* leave dialog open on failure */
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/auth/transition-date`, { transition_date: null }, { withCredentials: true });
      try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      setTransitionDate(null);
      setDialogOpen(false);
      if (typeof onChange === 'function') onChange(null);
    } catch {
      /* ignore */
    } finally {
      setSaving(false);
    }
  };

  const today = new Date();
  const transition = isValidIsoDate(transitionDate) ? new Date(transitionDate + 'T00:00:00') : null;
  const plusNinety = transition ? addDays(transition, 90) : null;

  const dialog = (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogContent className="max-w-sm" data-testid="transition-window-dialog">
        <DialogHeader>
          <DialogTitle>Expected separation or retirement date</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600 mb-1">
          Separation, ETS, retirement, or PCS — pick the date that anchors this chapter.
        </p>
        <Input
          data-testid="transition-window-date-input"
          type="date"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="mb-2"
        />
        <p className="text-[11px] text-slate-400">Saved to your account.</p>
        <DialogFooter className="gap-2">
          {transitionDate && (
            <Button
              data-testid="transition-window-clear-btn"
              variant="outline"
              size="sm"
              onClick={handleClear}
              disabled={saving}
              className="border-slate-300 text-slate-600"
            >
              Clear
            </Button>
          )}
          <Button
            data-testid="transition-window-save-btn"
            size="sm"
            onClick={handleSave}
            disabled={!isValidIsoDate(draft) || saving}
            className="bg-brand hover:bg-brand-hover text-white"
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // Minimized variant — a single calm line with an inline action. No date
  // display; used when the user has no transition date yet.
  if (variant === 'minimized') {
    return (
      <div
        data-testid="candidate-transition-window-strip"
        data-variant="minimized"
        className="mb-0 flex flex-wrap items-center gap-2 text-sm text-slate-500"
      >
        <span data-testid="transition-window-minimized-line">
          Set your expected separation or retirement date &mdash; it&apos;ll anchor your dashboard.
        </span>
        <button
          data-testid="transition-window-set-date-link"
          onClick={openDialog}
          className="text-sm font-medium text-brand hover:text-brand-hover underline-offset-2 hover:underline"
          type="button"
        >
          Set date
        </button>
        {dialog}
      </div>
    );
  }

  return (
    <Card
      data-testid="candidate-transition-window-strip"
      className="border border-slate-200 shadow-sm rounded-2xl overflow-hidden"
    >
      {/* ── Mobile compact layout (< sm) ────────────────────────────── */}
      <div className="sm:hidden px-4 py-3.5" data-testid="transition-window-mobile">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-brand-light flex items-center justify-center flex-shrink-0">
            <Calendar className="w-4 h-4 text-brand" strokeWidth={1.8} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-bold tracking-wider uppercase text-brand leading-none mb-1">
              Transition
            </p>
            {transition ? (
              <>
                <p className="text-sm font-semibold text-slate-900 truncate" data-testid="transition-window-mobile-date">
                  {formatLong(transition)}
                </p>
                <p className="text-xs text-slate-500 mt-0.5" data-testid="transition-window-mobile-remaining">
                  {remainingLabel(transition)}
                </p>
              </>
            ) : (
              <p className="text-sm text-slate-500" data-testid="transition-window-mobile-empty">
                Transition date not set
              </p>
            )}
          </div>
          <button
            data-testid={transition ? 'transition-window-edit-link' : 'transition-window-set-date-link'}
            onClick={openDialog}
            className="text-sm font-semibold text-brand hover:text-brand-hover flex-shrink-0"
            type="button"
          >
            {transition ? 'Edit' : 'Set date'}
          </button>
        </div>
      </div>

      {/* ── Desktop layout (>= sm) ──────────────────────────────────── */}
      <div className="hidden sm:flex px-5 sm:px-6 py-5 flex-row items-center gap-5">
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="w-11 h-11 rounded-xl bg-brand-light flex items-center justify-center">
            <Calendar className="w-5 h-5 text-brand" strokeWidth={1.8} />
          </div>
          <p className="text-xs font-bold tracking-wider uppercase text-brand leading-tight">
            Transition<br />Window
          </p>
        </div>

        {transition ? (
          <div className="flex-1 flex items-center gap-5" data-testid="transition-window-bar">
            <div className="flex flex-col items-start">
              <span className="text-xs text-slate-400">Today</span>
              <span className="text-sm font-semibold text-slate-900 whitespace-nowrap">{formatLong(today)}</span>
            </div>
            <div className="flex-1 h-px bg-slate-200 min-w-[16px]" aria-hidden="true" />
            <div className="flex flex-col items-center">
              <span className="text-xs text-slate-400">Transition</span>
              <span className="text-sm font-semibold text-slate-900 whitespace-nowrap">{formatLong(transition)}</span>
              <span className="text-[10px] text-slate-400 mt-0.5">{remainingLabel(transition)}</span>
            </div>
            <div className="flex-1 h-px bg-slate-200 min-w-[16px]" aria-hidden="true" />
            <div className="flex flex-col items-end">
              <span className="text-xs text-slate-400">+90 days</span>
              <span className="text-sm font-semibold text-slate-900 whitespace-nowrap">{formatLong(plusNinety)}</span>
            </div>
            <div className="w-px h-10 bg-slate-200 mx-1" aria-hidden="true" />
            <button
              data-testid="transition-window-edit-link"
              onClick={openDialog}
              className="text-sm font-semibold text-brand hover:text-brand-hover flex-shrink-0"
              type="button"
            >
              Edit
            </button>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-between gap-3" data-testid="transition-window-empty-state">
            <p className="text-sm text-slate-600">Transition date not set.</p>
            <button
              data-testid="transition-window-set-date-link"
              onClick={openDialog}
              className="text-sm font-semibold text-brand hover:text-brand-hover"
              type="button"
            >
              Set date
            </button>
          </div>
        )}
      </div>

      {dialog}
    </Card>
  );
}

export default TransitionWindowStrip;
